import React, {useContext, useState, useEffect} from 'react';
import {Card, Checkbox, CheckboxProps, Col, List, Row, Divider, Typography, Button, Flex} from "antd";
import {GlobalSteps, Sides, StatusContext, leftChaptersCount} from "../../../services/types";
import {getChapterByNumberContent, updateChapterOffsets} from "../../../services/api";
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';

interface LocalStatus {
    currentChapter: number;
    leftChapterID?: string;
    leftChapterName: string;
    rightChapterID?: string;
    rightChapterName: string;
    leftChapterHeadLines: string[];
    leftChapterTailLines: string[];
    rightChapterHeadLines: string[];
    rightChapterTailLines: string[];
    currentLeftHeadOffset: number;
    currentLeftTailOffset: number;
    currentRightHeadOffset: number;
    currentRightTailOffset: number;
}

const StepContent = () => {
    const statusContext = useContext(StatusContext);
    if (!statusContext) {
        throw new Error("missing context");
    }

    const navigate = useNavigate();

    // global status
    const { status, setStatus } = statusContext;

    // content comparison status
    const [localStatus, setLocalStatus] = useState<LocalStatus>({
        currentChapter: 0,
        leftChapterName: "",
        rightChapterName: "",
        leftChapterHeadLines: [],
        leftChapterTailLines: [],
        rightChapterHeadLines: [],
        rightChapterTailLines: [],
        currentLeftHeadOffset: 0,
        currentLeftTailOffset: 0,
        currentRightHeadOffset: 0,
        currentRightTailOffset: 0,
    });

    const sanitizeContent = (content: string): string => {
        const sanitized = DOMPurify.sanitize(content, { ALLOWED_TAGS: [] });
        return sanitized.replace(/&nbsp;/g, ' ');
    };

    const loadLeftRight = (side: Sides) => {
        let urlSide = side === Sides.Right ? 'right' as const : 'left' as const;

        if (!status.projectID) {
            console.error("Project ID is missing");
            setStatus({
                ...status,
                globalStep: GlobalSteps.Fail,
            });
            return;
        }

        return getChapterByNumberContent(status.projectID, urlSide, localStatus.currentChapter)
            .then((data) => {
                if (side == Sides.Left) {
                    setLocalStatus(prev => ({
                        ...prev,
                        leftChapterHeadLines: data.head,
                        leftChapterTailLines: data.tail,
                        leftChapterID: data.id,
                        leftChapterName: data.name,
                    }))
                }

                if (side == Sides.Right) {
                    setLocalStatus(prev => ({
                        ...prev,
                        rightChapterHeadLines: data.head,
                        rightChapterTailLines: data.tail,
                        rightChapterID: data.id,
                        rightChapterName: data.name,
                    }))
                }

                if (status.leftChapters && status.rightChapters) {
                    const percent = localStatus.currentChapter / ( (status.leftChapters?.length + status.rightChapters?.length) / 2) * 100;
                    setStatus({
                        ...status,
                        percent: percent,
                    })
                }
            })
            .catch(error => {
                console.error(error);
                setStatus({
                    ...status,
                    globalStep: GlobalSteps.Fail,
                })
            })
    };

    useEffect(() => {
        if (!status.projectID) return;

        if (localStatus.currentChapter >= leftChaptersCount(status)) {
            setStatus({
                ...status,
                globalStep: GlobalSteps.Generation,
            });
            navigate('/steps/3', { state: { wizardNav: true } });
            return;
        };

        Promise.all([
            loadLeftRight(Sides.Left),
            loadLeftRight(Sides.Right)
        ]).catch(error => {
            console.error('Failed to load chapter content:', error);
        });
    }, [status.projectID, localStatus.currentChapter, navigate]);

    const onCheckParagraph: CheckboxProps['onChange'] = (e) => {
        const checked = e.target.checked;
        const value = e.target.value;
        const delta = checked ? -1 : 1;

        switch (value) {
            case 'left-head':
                setLocalStatus(prev => ({
                    ...prev,
                    currentLeftHeadOffset: Math.max(0, prev.currentLeftHeadOffset + delta)
                }));
                break;
            case 'left-tail':
                setLocalStatus(prev => ({
                    ...prev,
                    currentLeftTailOffset: Math.max(0, prev.currentLeftTailOffset + delta)
                }));
                break;
            case 'right-head':
                setLocalStatus(prev => ({
                    ...prev,
                    currentRightHeadOffset: Math.max(0, prev.currentRightHeadOffset + delta)
                }));
                break;
            case 'right-tail':
                setLocalStatus(prev => ({
                    ...prev,
                    currentRightTailOffset: Math.max(0, prev.currentRightTailOffset + delta)
                }));
                break;
            default:
                break;
        }
    }


    const nextChapter = () => {
        if (!status.projectID) return;
        if (!localStatus.leftChapterID) return;
        if (!localStatus.rightChapterID) return;

        updateChapterOffsets(
            status.projectID,
            localStatus.currentChapter,
            localStatus.currentLeftHeadOffset,
            localStatus.currentLeftTailOffset,
            localStatus.currentRightHeadOffset,
            localStatus.currentRightTailOffset,
        ).catch(error => {
            console.error(error);
            setStatus({
                ...status,
                globalStep: GlobalSteps.Fail,
            })
        })

        setLocalStatus(prev => ({
            ...prev,
            currentLeftHeadOffset: 0,
            currentLeftTailOffset: 0,
            currentRightHeadOffset: 0,
            currentRightTailOffset: 0,
            currentChapter: prev.currentChapter+1,
        }));
    };

    const skipComparison = () => {
        setStatus({
            ...status,
            globalStep: GlobalSteps.Generation,
        });
        navigate('/steps/3', { state: { wizardNav: true } });
        return;
    }

    return (
        <Card bordered={false}>
            <Row gutter={16} style={{ height: '100%', marginBottom: 16 }}>
                <Col span={12} style={{ height: '100%' }}>
                    <Typography.Title level={5}>{localStatus.leftChapterName}</Typography.Title>
                </Col>
                <Col span={12} style={{ height: '100%' }}>
                    <Typography.Title level={5}>{localStatus.rightChapterName}</Typography.Title>
                </Col>
            </Row>
            <Row gutter={16} style={{ height: '100%' }}>
                <Col span={12} style={{ height: '100%' }}>
                    <List
                        bordered
                        style={{ height: '100%', overflow: 'auto' }}
                        dataSource={localStatus.leftChapterHeadLines}
                        renderItem={(line: string, index: number) => (
                            <List.Item>
                                <Checkbox
                                    value={"left-head"}
                                    onChange={onCheckParagraph}
                                    checked={localStatus.currentLeftHeadOffset <= index}
                                    disabled={localStatus.currentLeftHeadOffset != index && localStatus.currentLeftHeadOffset != index+1}>{sanitizeContent(line)}</Checkbox>
                            </List.Item>
                        )}
                    />
                </Col>
                <Col span={12} style={{ height: '50%' }}>
                    <List
                        bordered
                        style={{ height: '100%', overflow: 'auto' }}
                        dataSource={localStatus.rightChapterHeadLines}
                        renderItem={(line: string, index: number) => (
                            <List.Item>
                                <Checkbox
                                    value={"right-head"}
                                    onChange={onCheckParagraph}
                                    checked={localStatus.currentRightHeadOffset <= index}
                                    disabled={localStatus.currentRightHeadOffset != index && localStatus.currentRightHeadOffset != index+1}>{sanitizeContent(line)}</Checkbox>
                            </List.Item>
                        )}
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24} style={{ height: '100%' }}>
                    <Divider variant={"dashed"} style={{ borderColor: 'var(--color-accent)' }}/>
                </Col>
            </Row>
            <Row gutter={16} style={{ height: '100%', marginBottom: 16 }}>
                <Col span={12} style={{ height: '100%' }}>
                    <List
                        bordered
                        style={{ height: '100%', overflow: 'auto' }}
                        dataSource={localStatus.leftChapterTailLines}
                        renderItem={(line: string, index: number) => {
                            const realIndex = localStatus.leftChapterTailLines.length - 1 - index
                            return (
                                <List.Item>
                                    <Checkbox
                                        value={"left-tail"}
                                        onChange={onCheckParagraph}
                                        checked={localStatus.currentLeftTailOffset <= realIndex}
                                        disabled={localStatus.currentLeftTailOffset != realIndex && localStatus.currentLeftTailOffset != realIndex+1}>{sanitizeContent(line)}
                                    </Checkbox>
                                </List.Item>
                            )
                        }}
                    />
                </Col>
                <Col span={12} style={{ height: '50%' }}>
                    <List
                        bordered
                        style={{ height: '100%', overflow: 'auto' }}
                        dataSource={localStatus.rightChapterTailLines}
                        renderItem={(line: string, index: number) => {
                            const realIndex = localStatus.rightChapterTailLines.length - 1 - index
                            return (
                                <List.Item>
                                    <Checkbox
                                        value={"right-tail"}
                                        onChange={onCheckParagraph}
                                        checked={localStatus.currentRightTailOffset <= realIndex}
                                        disabled={localStatus.currentRightTailOffset != realIndex && localStatus.currentRightTailOffset != realIndex+1}>{sanitizeContent(line)}</Checkbox>
                                </List.Item>
                            )
                        }}
                    />
                </Col>
            </Row>
            <Row gutter={16} style={{ height: '100%' }}>
                <Col span={24} style={{ height: '100%', textAlign: 'left' }}>
                    <Flex align="flex-start" gap="small">
                        <Button type="primary" onClick={() => nextChapter()}>
                            Next chapter
                        </Button>
                        <Button onClick={() => skipComparison()}>
                            Skip content comparison
                        </Button>
                    </Flex> 
                </Col>
            </Row>
        </Card>
    )
}

export default StepContent;